<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted, computed } from 'vue';
import { useLayout } from './composables/layout';
import { useStates } from '../router/states';
import { useDefaultLogo } from '../common/composables/useDefaultLogo';
import userInitialDataService from '../views/pages/auth/services/user-initial-data.service';
import authService from '../views/pages/auth/services/authService';
import { UserInitialDataDto } from '../views/pages/auth/models/user-initial-data.interface';
import { CompanySwitchRequestDto } from '../views/pages/auth/models/company-switch.interface';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const toast = useToast();
const router = useRouter();
const { userState, spinnerState } = useStates();
const { layoutState, layoutConfig } = useLayout();
const { defaultLogo } = useDefaultLogo();

// Logo específico para el selector de compañías (siempre contifastBlack)
const companySelectorLogo = computed(() => '/layout/images/logo/contifastBlack.png');

const companies = ref<UserInitialDataDto[]>([]);
const selectedCompany = ref<UserInitialDataDto | null>(null);
const showCompanySelector = ref(false);
const companiesList = ref<UserInitialDataDto[]>([]);
const companyMenuRef = ref<HTMLElement | null>(null);
const companySelectorContainer = ref<HTMLElement | null>(null);

// Computed para determinar si el usuario tiene múltiples compañías
const hasMultipleCompanies = computed(() => companies.value.length > 1);

// Referencia reactiva para forzar la actualización cuando cambia el modo
const layoutMode = ref('');
const forceUpdate = ref(0); // Contador para forzar actualizaciones

// Variable reactiva para detectar pantallas pequeñas
const isSmallScreen = ref(false);

// Función para actualizar el estado de pantalla pequeña
const updateScreenSize = () => {
  isSmallScreen.value = window.innerWidth <= 991;
};

// Función para actualizar el modo de layout
const updateLayoutMode = () => {
  // Usar directamente el valor del composable de layout
  layoutMode.value = layoutConfig.menuMode.value;
};

// Propiedad computada para determinar si mostrar el logo completo en modo horizontal
const showFullInHorizontalMode = computed(() => {
  return layoutMode.value === 'horizontal';
});

// Determinar si estamos en modo cajón o revelar
const isDrawerOrRevealMode = computed(() => {
  return layoutMode.value === 'drawer' || layoutMode.value === 'reveal';
});

// Determinar si estamos en modo compacto o slim (solo logo)
const isCompactOrSlimMode = computed(() => {
  return (
    layoutMode.value === 'compact' ||
    layoutMode.value === 'compact-plus' ||
    layoutMode.value === 'slim' ||
    layoutMode.value === 'slim-plus'
  );
});

// Determinar si el sidebar está activo usando directamente el estado del layout
const isSidebarActive = computed(() => {
  return layoutState.sidebarActive.value;
});

// Función para determinar si el componente está dentro del sidebar
const isInSidebar = () => {
  // Verificar si el componente está dentro del sidebar
  if (!companySelectorContainer.value) return false;

  // Buscar el elemento padre que sea el sidebar
  let parent = companySelectorContainer.value.parentElement;
  while (parent) {
    if (parent.classList.contains('layout-sidebar')) {
      return true;
    }
    parent = parent.parentElement;
  }

  return false;
};

// Función para cargar las compañías del usuario
const loadCompanies = async () => {
  if (!(userState?.data?.value as any)?.email || companiesLoaded.value) return;

  spinnerState.showSpinner.value = true;
  try {
    const email = (userState.data.value as any)?.email;
    const data = await userInitialDataService.getUserInitialDataByEmail(email);
    companies.value = data;
    companiesLoaded.value = true;

    // Seleccionar la compañía actual
    if ((userState?.data?.value as any)?.id && companies.value.length > 0) {
      selectedCompany.value =
        companies.value.find((c) => c.id === (userState.data.value as any)?.id) || companies.value[0];
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: t('shared.error'),
      detail: t('shared.errorLoadingCompanies'),
      life: 3000,
    });
  } finally {
    spinnerState.showSpinner.value = false;
  }
};

// Función para cambiar de compañía
const changeCompany = async (company: UserInitialDataDto) => {
  if (company.id === (userState?.data?.value as any)?.id) {
    showCompanySelector.value = false;
    return;
  }

  // Mostrar advertencia si la suscripción no está activa, pero permitir continuar
  if (!company.subscriptionActive) {
    toast.add({
      severity: 'warn',
      summary: t('shared.warning'),
      detail: t('login.companyWithoutSubscription'),
      life: 4000,
    });
    // NO hacer return, permitir que continúe
  }

  spinnerState.showSpinner.value = true;
  try {
    // Crear la solicitud de cambio de compañía
    const switchRequest: CompanySwitchRequestDto = {
      email: (userState?.data?.value as any)?.email || '',
      userId: company.id,
      companyId: company.companyId,
    };

    // Llamar al servicio para cambiar de compañía y obtener un nuevo token
    const response = await authService.switchCompany(switchRequest);

    // Verificar si hay datos en la respuesta
    if (response && (response as any).token) {
      // Guardar el nuevo token en localStorage
      sessionStorage.setItem('authToken', (response as any).token);

      // Mostrar mensaje de éxito antes de recargar
      toast.add({
        severity: 'success',
        summary: t('shared.success'),
        detail: t('shared.companyChanged'),
        life: 2000,
      });

      // Esperar un momento para que se muestre el mensaje
      setTimeout(() => {
        // Recargar la página para que se carguen los datos iniciales con el nuevo token
        window.location.reload();
      }, 1000);
    } else {
      throw new Error('No se recibió un token válido del servidor');
    }
  } catch (error: any) {
    // Mostrar mensaje de error
    let errorMessage = t('shared.errorChangingCompany');
    if (error.response) {
      // Error de respuesta del servidor
      if (error.response.data && typeof error.response.data === 'string') {
        errorMessage += ': ' + error.response.data;
      } else if (error.response.status === 401) {
        errorMessage += ': No autorizado';
      } else if (error.response.status === 500) {
        errorMessage += ': Error interno del servidor';
      }
    } else if (error.request) {
      // Error de solicitud (no se recibió respuesta)
      errorMessage += ': No se recibió respuesta del servidor';
    } else if (error.message) {
      // Error general
      errorMessage += ': ' + error.message;
    }

    toast.add({
      severity: 'error',
      summary: t('shared.error'),
      detail: errorMessage,
      life: 5000,
    });
  } finally {
    spinnerState.showSpinner.value = false;
    showCompanySelector.value = false;
  }
};

// Función para manejar el clic en el logo/nombre de la empresa
const handleCompanyClick = () => {
  if (hasMultipleCompanies.value) {
    // Si tiene múltiples compañías, mostrar el selector
    toggleCompanySelector();
  } else {
    // Si solo tiene una compañía, navegar al dashboard
    router.push('/');
  }
};

// Función para alternar la visibilidad del selector
const toggleCompanySelector = () => {
  showCompanySelector.value = !showCompanySelector.value;
};

// Funciones para manejar eventos de mouse (opcionales)
const handleMouseEnter = () => {
  // Placeholder para futura funcionalidad
};

const handleMouseLeave = () => {
  // Placeholder para futura funcionalidad
};

// Referencia al contenedor del selector ya declarada anteriormente

// Función para manejar clics fuera del componente
const handleClickOutside = (event: MouseEvent) => {
  // Solo procesar si el dropdown está visible
  if (showCompanySelector.value && companySelectorContainer.value) {
    // Verificar si el clic fue fuera del contenedor
    if (!companySelectorContainer.value.contains(event.target as Node)) {
      showCompanySelector.value = false;
    }
  }
};

// Flag para controlar si ya se cargaron las compañías
const companiesLoaded = ref(false);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

watch(
  () => (userState?.data?.value as any)?.email,
  (newValue, oldValue) => {
    // Solo cargar compañías si el email cambió y no es la carga inicial
    if (newValue && newValue !== oldValue) {
      companiesLoaded.value = false; // Resetear el flag si el email cambió
      loadCompanies();
    } else if (newValue && !companiesLoaded.value) {
      // Si hay email pero no se han cargado las compañías todavía
      loadCompanies();
    }
  },
);

// Observador de mutaciones para detectar cambios en las clases del body
let bodyObserver: MutationObserver | null = null;

// Función para manejar cambios en el tamaño de la ventana
const handleResize = () => {
  updateLayoutMode();
  updateSidebarState();
  updateScreenSize();
};

// Función para forzar actualización de la UI cuando cambia el estado del sidebar
const updateSidebarState = () => {
  // Forzar actualización para asegurar que la UI refleje el estado correcto
  forceUpdate.value++;
};

onMounted(() => {
  // Cargar las compañías del usuario (solo una vez)
  if (!companiesLoaded.value) {
    loadCompanies();
  }

  // Configurar el observador de mutaciones para detectar cambios en las clases del body
  bodyObserver = new MutationObserver(() => {
    // Forzar actualización cuando cambie alguna clase del body
    updateLayoutMode();
    updateSidebarState();
  });

  // Iniciar la observación del body
  bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  // Agregar event listener para cambios en el tamaño de la ventana
  window.addEventListener('resize', handleResize);

  // Inicializar el modo de layout al montar el componente
  updateLayoutMode();

  // Inicializar el estado de pantalla pequeña
  updateScreenSize();

  // Configurar un watcher para el estado del sidebar del layout
  watch(
    () => layoutState.sidebarActive.value,
    () => {
      // Forzar actualización cuando cambie el estado del sidebar
      updateSidebarState();
    },
  );
});

// Limpiar los event listeners al desmontar el componente
onUnmounted(() => {
  // Eliminar el event listener de resize
  window.removeEventListener('resize', handleResize);

  // Detener el observador
  if (bodyObserver) {
    bodyObserver.disconnect();
  }
});
</script>

<template>
  <!-- Contenedor principal del selector de compañías que ahora integra el logo -->
  <div class="company-selector-container" ref="companySelectorContainer">
    <!-- Botón/Logo que muestra el selector de compañías o navega al dashboard -->
    <div
      v-tooltip.bottom="hasMultipleCompanies ? $t('shared.selectCompany') : ''"
      class="company-selector-button"
      v-ripple
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @click="handleCompanyClick">
      <!-- En pantallas pequeñas, siempre mostrar el formato específico para móvil independientemente del modo -->
      <div v-if="isSmallScreen" class="company-mobile-display">
        <img
          class="border-circle shadow-4 w-3rem h-3rem"
          :src="(userState?.data?.value as any)?.logoUrl || defaultLogo"
          alt="company-logo" />
        <div class="company-name-container">
          <span class="ml-2 text-white company-name">
            {{ (userState?.data?.value as any)?.nameCompany || '' }}
          </span>
        </div>
      </div>

      <!-- En pantallas normales, aplicar la lógica según el modo -->
      <template v-else>
        <!-- Para modos cajón y revelar -->
        <template v-if="isDrawerOrRevealMode">
          <div class="company-full-display" v-show="layoutState.sidebarActive.value">
            <img
              class="border-circle shadow-4 w-3rem h-3rem"
              :src="(userState?.data?.value as any)?.logoUrl ? (userState?.data?.value as any)?.logoUrl : defaultLogo"
              alt="company-logo" />
            <div class="company-name-container">
              <span class="ml-2 text-white company-name">
                {{ (userState?.data?.value as any)?.nameCompany || '' }}
              </span>
            </div>
          </div>

          <div
            class="company-slim-display"
            :class="{ 'justify-content-end': layoutMode === 'reveal' }"
            style="width: 100%"
            v-show="!layoutState.sidebarActive.value">
            <img
              class="border-circle shadow-4 w-3rem h-3rem"
              :src="(userState?.data?.value as any)?.logoUrl ? (userState?.data?.value as any)?.logoUrl : defaultLogo"
              alt="company-logo" />
          </div>
        </template>

        <!-- Para el resto de modos, usar la lógica existente -->
        <template v-else>
          <!-- Modos compactos y slim: solo logo -->
          <div v-if="isCompactOrSlimMode" class="company-slim-display" style="display: flex">
            <img
              class="border-circle shadow-4 w-3rem h-3rem"
              :src="(userState?.data?.value as any)?.logoUrl ? (userState?.data?.value as any)?.logoUrl : defaultLogo"
              alt="company-logo" />
          </div>

          <!-- Para todos los demás modos: logo con texto -->
          <div v-else class="company-full-display" style="display: flex">
            <img
              class="border-circle shadow-4 w-3rem h-3rem"
              :src="(userState?.data?.value as any)?.logoUrl ? (userState?.data?.value as any)?.logoUrl : defaultLogo"
              alt="company-logo" />
            <div class="company-name-container">
              <span class="ml-2 text-white company-name">
                {{ (userState?.data?.value as any)?.nameCompany || '' }}
              </span>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Dropdown de selección de compañías con transición -->
    <div
      v-if="hasMultipleCompanies"
      v-show="showCompanySelector"
      class="company-selector-dropdown"
      :class="{ 'company-dropdown-visible': showCompanySelector }">
      <div v-if="companies.length === 0" class="no-companies">
        {{ $t('shared.noCompaniesFound') }}
      </div>
      <ul v-else class="company-list list-none p-0 m-0">
        <li
          v-for="company in companies"
          :key="company.id"
          :class="{
            selected: company.id === (userState?.data?.value as any)?.id,
            'company-warning-border': !company.subscriptionActive,
          }">
          <a
            @click="changeCompany(company)"
            class="p-ripple company-item flex align-items-center transition-duration-150 transition-all px-3 py-2 cursor-pointer"
            v-ripple>
            <div class="company-logo">
              <img :src="company.companyLogo || companySelectorLogo" :alt="company.companyName" />
            </div>
            <div class="company-info">
              <div class="company-name">{{ company.companyName }}</div>
              <div class="company-id text-color-secondary">{{ company.companyIdentification }}</div>
              <div v-if="!company.subscriptionActive" class="company-warning-text">
                <i class="pi pi-exclamation-triangle"></i>
                <span>{{ $t('login.subscriptionInactive') }}</span>
              </div>
            </div>
            <i v-if="company.id === (userState?.data?.value as any)?.id" class="pi pi-check ml-auto"></i>
            <i v-else class="pi pi-chevron-right ml-auto text-color-secondary"></i>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
/* Contenedor principal del selector de compañías */
.company-selector-container {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 100%;
  flex: 1;
}

/* Estilos específicos para el selector de empresa en el sidebar */
:deep(.layout-sidebar) .company-selector-container {
  margin: 0;
  padding: 0.5rem;
  width: 100%;
}

:deep(.layout-sidebar) .company-name {
  color: white;
  margin-left: 0.5rem;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 120px;
  display: inline-block;
  line-height: 1.2;
}

:deep(.layout-sidebar) img {
  max-width: 3rem;
  max-height: 3rem;
}

/* Corregir problema de doble logo en modo revelar y drawer */
:deep(.layout-reveal) .company-selector-container,
:deep(.layout-drawer) .company-selector-container {
  display: flex;
}

/* Ocultar el componente en el topbar cuando el sidebar está activo */
:deep(.layout-reveal.layout-sidebar-active) .layout-topbar .company-selector-container,
:deep(.layout-drawer.layout-sidebar-active) .layout-topbar .company-selector-container {
  display: none !important;
}

/* Asegurar que el componente en el sidebar se muestre correctamente */
:deep(.layout-reveal.layout-sidebar-active) .layout-sidebar .company-selector-container,
:deep(.layout-drawer.layout-sidebar-active) .layout-sidebar .company-selector-container {
  display: flex !important;
}

/* Estilos para los modos cajón y revelar */
.company-full-display {
  display: flex;
}

.company-slim-display {
  display: flex;
}

/* Alineación para el modo revelar */
.company-slim-display.justify-content-end {
  justify-content: flex-end;
}

/* Estilos para el botón/logo del selector */
.company-selector-button {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--topbar-item-text-color);
  transition:
    background-color 0.2s,
    color 0.2s;
  text-decoration: none;
  max-width: 100%;
  width: 100%;
}

/* Estilos para el dropdown del selector */
.company-selector-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 300px;
  max-height: 400px;
  overflow-y: auto;
  background-color: var(--surface-overlay) !important;
  border-radius: var(--border-radius);
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.2) !important; /* Misma sombra que el selector de idiomas */
  z-index: 1000;
  margin-top: 0.5rem;
  padding-top: 5px;
  padding-bottom: 5px;
  opacity: 0;
  transform: translateY(-10px);
  transition:
    opacity 0.2s,
    transform 0.2s;
}

.company-dropdown-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Mensaje cuando no hay compañías */
.no-companies {
  padding: 1rem;
  text-align: center;
  color: var(--text-color-secondary);
  font-style: italic;
}

/* Lista de compañías */
.company-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Elemento individual de compañía */
.company-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition:
    background-color 0.2s,
    color 0.2s,
    box-shadow 0.2s;
  border-radius: var(--border-radius);
  margin: 2px 4px;
  position: relative;
}

.company-item:hover {
  background-color: var(--surface-hover);
}

/* Estilo para elemento seleccionado con mejor contraste */
li.selected .company-item {
  /* Aplicar el mismo estilo que .disable-link para tema claro */
  background-color: lightgray !important;
  color: black !important;
  pointer-events: auto; /* Mantener clickable a diferencia de disable-link */
  position: relative;
  font-weight: normal;
}

/* Estilo para tema oscuro */
:root[data-theme='dark'] li.selected .company-item {
  background-color: white !important;
  color: black !important;
}

/* Asegurar que todos los textos dentro del elemento seleccionado sean negros en tema oscuro */
:root[data-theme='dark'] li.selected .company-name,
:root[data-theme='dark'] li.selected .company-id,
:root[data-theme='dark'] li.selected .text-color-secondary {
  color: black !important;
}

/* Estilo para el icono de verificación */
.pi-check {
  color: var(--primary-color) !important; /* Color primario para el icono de verificación */
  font-size: 1rem;
}

/* Logo de la compañía en el dropdown */
.company-logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  flex-shrink: 0;
  border: 1px solid var(--surface-border);
}

.company-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Información de la compañía */
.company-info {
  flex: 1;
  overflow: hidden;
}

.company-name {
  font-weight: bold;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  color: var(--text-color);
  line-height: 1.2;
}

.company-id {
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Icono de verificación para la compañía seleccionada */
.pi-check {
  color: var(--primary-color);
  margin-left: 0.5rem;
}

/* Estilos para compañías deshabilitadas */
li.disabled .company-item {
  opacity: 0.6;
  pointer-events: none;
  background-color: var(--surface-100);
}

li.disabled .company-name,
li.disabled .company-id {
  color: var(--text-color-secondary);
}

.company-warning-text {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  color: var(--orange-500);
  font-size: 0.7rem;
  font-weight: 500;
}

.company-warning-text i {
  font-size: 0.75rem;
}

/* Icono de dropdown */
.company-dropdown-icon {
  font-size: 0.75rem;
}

/* Estilos específicos para mantener la compatibilidad con el tema */
:deep(.layout-topbar-logo) {
  display: flex;
  align-items: center;
  text-decoration: none;
  max-width: 100%;
  width: 100%;
}

/* Comportamiento responsivo */
@media screen and (max-width: 991px) {
  .company-selector-dropdown {
    position: fixed;
    top: 60px;
    left: 0;
    width: 100%;
    border-radius: 0;
    z-index: 1100;
  }

  /* En modo móvil, asegurar que el dropdown aparezca sobre otros elementos */
  .company-selector-container {
    position: static;
  }
}

/* Estilos para el selector de compañía */
.company-selector-button {
  display: flex;
  align-items: center;
}

/* Estilos para el contenedor con logo y texto */
.company-full-display {
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  padding-left: 10px;
}

.company-name-container {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

/* Estilos para el contenedor solo con logo */
.company-slim-display {
  display: flex;
  justify-content: center;
  width: 100%;
}

/* Estilos específicos para pantallas pequeñas/móviles */
.company-mobile-display {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  max-width: calc(100% - 3rem); /* Limitar ancho para dejar espacio para el botón toggle */
  padding-left: 0.5rem;
  margin-right: 3rem; /* Espacio a la derecha para el botón de toggle */
}

/* Ajustes específicos para el nombre de la compañía en pantallas pequeñas */
.company-mobile-display .company-name-container {
  max-width: calc(100% - 3.5rem); /* Dejar espacio para el logo */
  overflow: hidden;
}

.company-mobile-display .company-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Estilos para el texto del nombre de la compañía */
.company-name {
  display: inline-block;
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
  max-width: 100%;
}
</style>
